import React from "react";
import APIService from "../../Service/APIService";
import { Button, Badge, Input, Flex, Spin, Pagination, Skeleton } from "antd";
import {
  LoadingOutlined,
  ExclamationCircleFilled,
  CheckCircleFilled,
  ClockCircleFilled,
  FilterOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import TitleBar from "../../common/title-bar/title-bar.js";
import GenericDashboard from "../GenericDashboard/GenericDashboard.js";
import { useScreen } from "../Utils/Screens.js";
import DateRangeFilter from "../Orders/DateRangeFilter.js";
import FilteredViewChip from "../../common/filtered-view-chip/index.js";

// AABI "Filtered from Analytics" scope (§8.5): read from the URL at the
// boundary, validated, and applied inside the same filtered, paginated
// dashboard query on every request — so the scope survives status changes,
// pagination, page-size changes and keyword search. The chip labels are
// built from the backend's canonical `appliedFilters` display metadata, with
// the URL ids as a fallback until the first response arrives.
const AABI_SCOPE_KEYS = [
  "manufacturerId",
  "categoryId",
  "productGroupId",
  "productId",
  "accountId",
  "stateId",
  "distributorId",
  "regionId",
  "agingBucket",
];

// Executive Dashboard link parameters (Phase 2 section 8.1). Each is read,
// validated at this boundary, and sent on every request; an invalid value is
// dropped rather than forwarded.
const AGING_BUCKET_LABELS = {
  current: "Not yet due",
  "1_30": "1-30 days past due",
  "31_60": "31-60 days past due",
  "61_90": "61-90 days past due",
  "90_plus": "90+ days past due",
  no_due_date: "No due date",
};
const STATUS_LABELS = {
  open_balance: "Open Balance",
  past_due: "Past Due",
  fully_paid: "Fully Paid",
  voided: "Voided",
  sent_to_collection: "Sent To Collection",
};
const STATUS_PARAMS = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([param, label]) => [label, param]),
);
const COMPLETION_LABELS = {
  completed: "Completed",
  pending: "Pending",
  all: "Completed and pending",
};
const DEFAULT_COMPLETION = "completed";
// Keys that only ever arrive from another page, removed together by the chip.
const IMPORTED_EXTRA_KEYS = ["startDate", "endDate", "completion", "population", "from"];

const readAabiScope = (search) => {
  const params = new URLSearchParams(search);
  const parseId = (key) => {
    const raw = params.get(key);
    if (raw === null) return null;
    const str = String(raw).trim();
    return /^\d+$/.test(str) && Number(str) > 0 ? str : null;
  };
  const parseToken = (key) => {
    const str = String(params.get(key) ?? "").trim();
    return /^[\w-]{1,32}$/.test(str) ? str : null;
  };
  const startDate = params.get("startDate");
  const endDate = params.get("endDate");
  const hasDates = Boolean(startDate && endDate);
  const rawAging = params.get("agingBucket");
  const rawStatus = params.get("status");
  const rawCompletion = params.get("completion");
  const rawFrom = params.get("from");
  return {
    regionId: parseId("regionId"),
    agingBucket: rawAging && AGING_BUCKET_LABELS[rawAging] ? rawAging : null,
    status: rawStatus && STATUS_LABELS[rawStatus] ? rawStatus : null,
    completion: rawCompletion && COMPLETION_LABELS[rawCompletion] ? rawCompletion : null,
    population: ["sales", "receivables", "billed", "pending_invoices"].includes(params.get("population")) ? params.get("population") : null,
    from: rawFrom === "dashboard" ? "dashboard" : null,
    manufacturerId: parseId("manufacturerId"),
    categoryId: parseId("categoryId"),
    productGroupId: parseId("productGroupId"),
    productId: parseId("productId"),
    accountId: parseId("accountId"),
    // State ids may be codes ("TX") rather than numbers.
    stateId: parseToken("stateId"),
    distributorId: parseId("distributorId"),
    startDate: hasDates ? startDate : null,
    endDate: hasDates ? endDate : null,
  };
};

const InvoiceDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const aabiScope = React.useMemo(() => readAabiScope(location.search), [location.search]);
  // The chip also shows for date-only, status-only and completion-only links
  // (Phase 2 section 8.1); `from` alone has nothing to show.
  const hasAabiScope =
    AABI_SCOPE_KEYS.some((key) => aabiScope[key]) ||
    Boolean(aabiScope.startDate || aabiScope.completion || aabiScope.population) ||
    Boolean(aabiScope.status && aabiScope.from);

  const [formattedInvoices, setFormattedInvoices] = React.useState([]);
  const [appliedFilters, setAppliedFilters] = React.useState(null);
  const [totalCount, setTotalCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const requestIdRef = React.useRef(0);
  const [dateRange, setDateRange] = React.useState(null);

  const isPhone = !useScreen("sm");

  const roundToTwo = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100) / 100;
  };

  const hasInvoiceSentToCollection = (invoice) => {
    const value = invoice?.HasSentToCollection;
    if (value === true || value === 1) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1";
    }
    return false;
  };

  const getInvoiceTotalWithCharges = (invoice) =>
    roundToTwo(
      roundToTwo(invoice?.InvoiceTotal) +
        roundToTwo(invoice?.ShippingCost) +
        roundToTwo(invoice?.CardProcessingFee) +
        roundToTwo(invoice?.BankProcessingFee)
    );

  const getInvoiceDashboardTotal = (invoice) =>
    hasInvoiceSentToCollection(invoice)
      ? roundToTwo(invoice?.InvoiceTotal)
      : getInvoiceTotalWithCharges(invoice);

  const getInvoiceDashboardStatus = (invoice) => {
    const invoiceTotalWithCharges = getInvoiceTotalWithCharges(invoice);
    const totalPaymentsApplied = roundToTwo(invoice?.TotalPaymentsApplied);
    const hasSentToCollection = hasInvoiceSentToCollection(invoice);
    const isVoided = Boolean(invoice?.IsVoided);
    const dueDate = invoice?.InvoiceDueDate ? dayjs(invoice.InvoiceDueDate) : null;
    const isPastDue =
      !dueDate || !dueDate.isValid()
        ? false
        : dueDate.startOf("day").isBefore(dayjs().startOf("day"));

    if (isVoided) {
      return {
        text: "Voided",
        statusClass: "voided",
        textColor: "lightText",
      };
    }

    // Saved but not completed invoices (the Pending completion filter) carry a
    // Pending badge. Only an explicit false counts, so rows from endpoints that
    // do not send the flag keep their normal status.
    if (invoice?.InvoiceCompleted === false) {
      return {
        text: "Pending",
        statusClass: "pending",
        textColor: "primaryColor",
      };
    }

    if (hasSentToCollection) {
      return {
        text: "Sent To Collection",
        statusClass: "collections",
        textColor: "primaryColor",
      };
    }

    if (totalPaymentsApplied >= invoiceTotalWithCharges) {
      return {
        text: "Fully Paid",
        statusClass: "success",
        textColor: "successColor",
      };
    }

    if (isPastDue) {
      return {
        text: "Past Due",
        statusClass: "failure",
        textColor: "failureColor",
      };
    }

    return {
      text: "Open Balance",
      statusClass: "warning",
      textColor: "warningColor",
    };
  };

  const formatInvoices = (data) => {
    return (data || []).map((invoice) => {
      const totalDue = getInvoiceDashboardTotal(invoice);
      const dashboardInvoiceStatus = getInvoiceDashboardStatus(invoice);
      return {
        ...invoice,
        InvoiceNumber:
          invoice?.InvoiceNumber === null || invoice?.InvoiceNumber === undefined
            ? ""
            : invoice?.InvoiceNumber,
        InvoiceDate: dayjs(invoice.InvoiceDate).format("MM-DD-YYYY"),
        InvoiceDueDate: dayjs(invoice.InvoiceDueDate).format("MM-DD-YYYY"),
        InvoiceTotalNumber: totalDue,
        InvoiceTotal: `$${parseFloat(totalDue).toLocaleString("en-US", {
          maximumFractionDigits: 2,
        })}`,
        Icon: (
          <Badge
            count={
              dashboardInvoiceStatus.text === "Past Due" ? (
                <ExclamationCircleFilled style={{ color: "#ff0000" }} />
              ) : dashboardInvoiceStatus.text === "Open Balance" ? (
                <ClockCircleFilled style={{ color: "#ff9202" }} />
              ) : dashboardInvoiceStatus.text === "Pending" ? (
                <ClockCircleFilled style={{ color: "#2663eb" }} />
              ) : (
                <CheckCircleFilled style={{ color: "#27a907" }} />
              )
            }
            style={{ fontSize: "22px" }}
          />
        ),
        DashboardInvoiceStatus: dashboardInvoiceStatus,
      };
    });
  };

  const getStatusParam = (filter) => {
    switch (filter) {
      case "Open Balance":
        return "open_balance";
      case "Fully Paid":
        return "fully_paid";
      case "Past Due":
        return "past_due";
      case "Voided":
        return "voided";
      case "Sent To Collection":
        return "sent_to_collection";
      default:
        return "non_voided";
    }
  };

  const buildInvoiceUrl = (page, size, filters, statusFilter, search, completionFilter) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(size));
    // Imported AABI scope (§8.5) intersects with everything below — it never
    // replaces any predicate and is sent on every request.
    AABI_SCOPE_KEYS.forEach((key) => {
      if (aabiScope[key]) params.set(key, aabiScope[key]);
    });
    // A local date filter overrides the imported AABI dates; imported dates
    // apply until the user picks their own range.
    const effectiveFilters =
      filters?.startDate && filters?.endDate
        ? filters
        : aabiScope.startDate && aabiScope.endDate
          ? { startDate: aabiScope.startDate, endDate: aabiScope.endDate }
          : filters;
    if (effectiveFilters?.startDate)
      params.set("startDate", effectiveFilters.startDate);
    if (effectiveFilters?.endDate)
      params.set("endDate", effectiveFilters.endDate);
    params.set("status", getStatusParam(statusFilter));
    // Completion and population only travel when they differ from today's
    // behavior, so a request without the new filters is unchanged.
    if (completionFilter && completionFilter !== DEFAULT_COMPLETION) {
      params.set("completion", completionFilter);
    }
    if (aabiScope.population) params.set("population", aabiScope.population);
    // Keyword search runs inside the same filtered, authorized, paginated
    // query (§7.3) — the standalone search endpoint drops every dashboard
    // filter and is no longer used.
    if (search) params.set("search", search);
    return `getinvoicedashboard?${params.toString()}`;
  };

  const fetchInvoicesPage = async (
    page,
    size,
    filters,
    statusFilter,
    search,
    completionFilter
  ) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        buildInvoiceUrl(page, size, filters, statusFilter, search, completionFilter)
      );
      if (requestId !== requestIdRef.current) return;
      const payload = response?.data ?? {};
      // Canonical names for the "Filtered from Analytics" chip (§8.5); null when unscoped.
      setAppliedFilters(payload?.appliedFilters ?? null);
      const rows = payload?.data ?? [];
      const total = payload?.totalCount ?? rows.length;
      const pages = payload?.totalPages ?? Math.ceil(total / (size || 1));
      const formatted = formatInvoices(rows);
      setFormattedInvoices(formatted);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (err) {
      console.error("Failed to fetch invoice dashboard data:", err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const [show, setShow] = React.useState(true);
  // Status and completion are seeded from the URL and written back to it, so a
  // headline number can deep-link to them and a refresh keeps them.
  const [selectedFilter, setSelectedFilter] = React.useState(
    STATUS_LABELS[aabiScope.status] ?? ""
  );
  const [completion, setCompletion] = React.useState(
    aabiScope.completion ?? DEFAULT_COMPLETION
  );
  const [isFilter, setIsFilter] = React.useState(Boolean(aabiScope.status));

  React.useEffect(() => {
    setSelectedFilter(STATUS_LABELS[aabiScope.status] ?? "");
    setIsFilter(Boolean(aabiScope.status));
    setCurrentPage(1);
  }, [aabiScope.status]);

  React.useEffect(() => {
    setCompletion(aabiScope.completion ?? DEFAULT_COMPLETION);
    setCurrentPage(1);
  }, [aabiScope.completion]);

  // A new link with new dates must not be overridden by a range picked for the
  // previous one.
  React.useEffect(() => {
    setDateRange(null);
  }, [aabiScope.startDate, aabiScope.endDate]);

  React.useEffect(() => {
    fetchInvoicesPage(
      currentPage,
      pageSize,
      dateRange,
      selectedFilter,
      searchKeyword,
      completion
    );
  }, [
    searchKeyword,
    currentPage,
    pageSize,
    dateRange,
    selectedFilter,
    completion,
    aabiScope,
  ]);

  const writeSearchParams = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const handleFilter = (filter) => {
    if (searchKeyword) {
      requestIdRef.current += 1;
      setSearchKeyword("");
    }
    if (filter === "Clear") {
      setIsFilter(false);
      setSelectedFilter("");
      setCurrentPage(1);
      writeSearchParams((params) => params.delete("status"));
      return;
    }
    setIsFilter(true);
    setSelectedFilter(filter);
    setCurrentPage(1);
    writeSearchParams((params) => params.set("status", STATUS_PARAMS[filter]));
  };

  const handleCompletion = (value) => {
    setCompletion(value);
    setCurrentPage(1);
    writeSearchParams((params) => {
      if (value === DEFAULT_COMPLETION) params.delete("completion");
      else params.set("completion", value);
    });
  };

  const handleSearch = (val) => {
    // Search is applied inside the scoped dashboard query (see buildInvoiceUrl)
    // so imported AABI scope and dates survive keyword search (§7.3).
    const keyword = (val || "").trim();
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  // Clearing the chip removes ONLY the imported AABI scope/date keys — local
  // status/search filters are untouched (§8.5).
  const clearAabiScope = () => {
    const params = new URLSearchParams(location.search);
    [...AABI_SCOPE_KEYS, ...IMPORTED_EXTRA_KEYS].forEach((key) =>
      params.delete(key)
    );
    // The status is the page's own filter too; it is imported (and cleared
    // with the chip) only when the link came from another page.
    if (aabiScope.from) params.delete("status");
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
    setCurrentPage(1);
  };
  const handleDateFilterChange = async (dateRange) => {
    setDateRange(dateRange);
    setCurrentPage(1);
  };

  const handlePaginationChange = (page, size) => {
    const nextSize = size || pageSize;
    const sizeChanged = nextSize !== pageSize;
    if (sizeChanged) {
      setPageSize(nextSize);
      setCurrentPage(1);
      return;
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // The status and completion controls, shared by the phone and desktop rows.
  const statusButton = (label, badge) => (
    <Button
      key={label}
      ghost={selectedFilter === label ? false : true}
      style={{ color: "#444", borderColor: "#444" }}
      onClick={() => handleFilter(label)}
    >
      {badge && <Badge count={show ? badge : 0} />}
      {label}
    </Button>
  );

  const statusButtons = (
    <>
      {statusButton("Open Balance", <ClockCircleFilled style={{ color: "#ff9202" }} />)}
      {statusButton("Fully Paid", <CheckCircleFilled style={{ color: "#27a907" }} />)}
      {statusButton("Past Due", <ExclamationCircleFilled style={{ color: "#ff0000" }} />)}
      {statusButton("Voided")}
      {statusButton("Sent To Collection")}
      {isFilter && (
        <Button
          ghost
          style={{ color: "#444", borderColor: "#444" }}
          onClick={() => handleFilter("Clear")}
        >
          Clear Filters <FilterOutlined />
        </Button>
      )}
      <div
        role="group"
        aria-label="Invoice completion"
        style={{ display: "flex", gap: "10px" }}
      >
        {Object.entries(COMPLETION_LABELS).map(([value, label]) => (
          <Button
            key={value}
            ghost={completion === value ? false : true}
            aria-pressed={completion === value}
            style={{ color: "#444", borderColor: "#444" }}
            onClick={() => handleCompletion(value)}
          >
            {value === "all" ? "All" : label}
          </Button>
        ))}
      </div>
    </>
  );

  // The picker shows the dates imported from the URL (it used to ignore them),
  // and re-seeds when a new link changes them.
  const urlDateRange = React.useMemo(
    () =>
      aabiScope.startDate && aabiScope.endDate
        ? [dayjs(aabiScope.startDate), dayjs(aabiScope.endDate)]
        : null,
    [aabiScope.startDate, aabiScope.endDate]
  );

  const renderedRowData = formattedInvoices;

  return (
    <div>
      <TitleBar
        label="Invoices"
        invoiceNumber={
          loading ? (
            <Skeleton.Avatar
              active
              shape="circle"
              size={24}
              style={{ width: 24 }}
            />
          ) : (
            totalCount
          )
        }
        rightFilterBarComponents={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Input.Search
              placeholder="Search"
              // suffix={<SearchOutlined />}
              allowClear
              style={{ height: "36px" }}
              // onSearch={value => setSearchKeyword(value)}
              onChange={(e) => {
                if (e.target.value === "") {
                  handleSearch("");
                }
              }}
              onSearch={(value) => handleSearch(value)}
            />
          </div>
        }
      />

      {hasAabiScope && (
        <FilteredViewChip
          label={
            aabiScope.from === "dashboard"
              ? "Filtered from Dashboard"
              : "Filtered from Analytics"
          }
          clearLabel={
            aabiScope.from === "dashboard"
              ? "Clear Dashboard filter"
              : "Clear Analytics filter"
          }
          onClear={clearAabiScope}
          items={[
            aabiScope.manufacturerId &&
              (appliedFilters?.manufacturerName ?? `Manufacturer #${aabiScope.manufacturerId}`),
            aabiScope.categoryId &&
              (appliedFilters?.categoryName ?? `Category #${aabiScope.categoryId}`),
            aabiScope.productGroupId &&
              (appliedFilters?.productGroupName ?? `Group #${aabiScope.productGroupId}`),
            aabiScope.productId &&
              (appliedFilters?.productName
                ? `${appliedFilters.productName}${appliedFilters.productCode ? ` (${appliedFilters.productCode})` : ""}`
                : `Product #${aabiScope.productId}`),
            aabiScope.accountId &&
              (appliedFilters?.accountName ?? `Account #${aabiScope.accountId}`),
            aabiScope.stateId &&
              (appliedFilters?.stateName ?? `State ${aabiScope.stateId}`),
            aabiScope.distributorId &&
              (appliedFilters?.distributorName ?? `Distributor #${aabiScope.distributorId}`),
            aabiScope.regionId &&
              `${appliedFilters?.regionName ?? `Region #${aabiScope.regionId}`} region`,
            aabiScope.status &&
              aabiScope.from &&
              (appliedFilters?.status ?? STATUS_LABELS[aabiScope.status]),
            aabiScope.agingBucket &&
              (appliedFilters?.agingBucket ?? AGING_BUCKET_LABELS[aabiScope.agingBucket]),
            aabiScope.population === "billed" && "Billed",
            aabiScope.population === "pending_invoices" && "Pending invoices",
            aabiScope.completion &&
              (appliedFilters?.completion ?? COMPLETION_LABELS[aabiScope.completion]),
            aabiScope.startDate &&
              `${dayjs(aabiScope.startDate).format("MMM D, YYYY")} - ${dayjs(
                aabiScope.endDate
              ).format("MMM D, YYYY")}`,
          ]}
        />
      )}

      {isPhone ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div>
            <DateRangeFilter
              key={`${aabiScope.startDate ?? ""}|${aabiScope.endDate ?? ""}`}
              defaultRange={urlDateRange}
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              overflowX: "scroll",
              gap: "16px",
              scrollbarWidth: "none",
            }}
          >
            {statusButtons}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "40px 0px 10px 0px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <DateRangeFilter
              key={`${aabiScope.startDate ?? ""}|${aabiScope.endDate ?? ""}`}
              defaultRange={urlDateRange}
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
            {statusButtons}
          </div>
        </div>
      )}

      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      <GenericDashboard data={renderedRowData} screen="InvoiceDashboardNew" />
      {!loading && totalCount > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={["20", "50", "100"]}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;
